import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { OctroGoldCoin } from 'components/OctroGoldCoin';
import {
  winnerChipPosition,
  potZoneCoord,
  type SeatLayoutProfile,
  type SeatPosition,
} from 'constants/seatLayout';

interface PotCollectAnimationProps {
  toSeatNo: number;
  seatLayoutMap: Map<number, SeatPosition>;
  seatProfile?: SeatLayoutProfile;
  amount: number;
  onStart?: () => void;
  onChipLaunch?: () => void;
  onDone?: () => void;
}

/** Pot chips fly from center pot zone to the winning player's seat */
export default function PotCollectAnimation({
  toSeatNo,
  seatLayoutMap,
  seatProfile = 'desktop',
  amount,
  onStart,
  onChipLaunch,
  onDone,
}: PotCollectAnimationProps) {
  const [visible, setVisible] = useState(true);
  const origin = potZoneCoord(seatProfile);
  const target = winnerChipPosition(toSeatNo, seatLayoutMap, seatProfile);
  const chipCount = Math.min(6, Math.max(3, Math.ceil(amount / 15)));
  const onDoneRef = useRef(onDone);
  const onStartRef = useRef(onStart);
  const onChipLaunchRef = useRef(onChipLaunch);
  const completedRef = useRef(0);
  const doneCalledRef = useRef(false);

  useEffect(() => {
    onDoneRef.current = onDone;
  }, [onDone]);

  useEffect(() => {
    onStartRef.current = onStart;
  }, [onStart]);

  useEffect(() => {
    onChipLaunchRef.current = onChipLaunch;
  }, [onChipLaunch]);

  useEffect(() => {
    completedRef.current = 0;
    doneCalledRef.current = false;
    onStartRef.current?.();
    const chipTimers = Array.from({ length: chipCount }).map((_, i) =>
      window.setTimeout(() => {
        onChipLaunchRef.current?.();
      }, i * 85)
    );
    const safetyTimer = window.setTimeout(() => {
      if (doneCalledRef.current) return;
      doneCalledRef.current = true;
      setVisible(false);
      onDoneRef.current?.();
    }, 1100 + chipCount * 95 + 200);
    return () => {
      chipTimers.forEach((id) => window.clearTimeout(id));
      window.clearTimeout(safetyTimer);
    };
  }, [chipCount, toSeatNo, amount]);

  const handleChipComplete = () => {
    completedRef.current += 1;
    if (completedRef.current < chipCount || doneCalledRef.current) return;
    doneCalledRef.current = true;
    setVisible(false);
    onDoneRef.current?.();
  };

  if (amount <= 0 || !visible) return null;

  const center = { x: '-50%', y: '-50%' };

  return (
    <AnimatePresence>
      {Array.from({ length: chipCount }).map((_, i) => (
        <motion.div
          key={`pot-to-${toSeatNo}-${i}`}
          className="pointer-events-none absolute z-[55]"
          initial={{
            left: `${origin.x}%`,
            top: `${origin.y}%`,
            x: center.x,
            y: center.y,
            opacity: 0,
            scale: 0.35,
          }}
          animate={{
            left: `${target.x}%`,
            top: `${target.y}%`,
            x: center.x,
            y: center.y,
            opacity: [0, 1, 1, 0.9, 0],
            scale: [0.35, 1.1, 1, 0.92, 0.5],
          }}
          exit={{ opacity: 0, scale: 0.3 }}
          transition={{
            duration: 1.05,
            delay: i * 0.085,
            ease: [0.22, 0.61, 0.36, 1],
          }}
          onAnimationComplete={handleChipComplete}
        >
          <OctroGoldCoin size={24} />
        </motion.div>
      ))}
    </AnimatePresence>
  );
}
